import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth, API_BASE as BACKEND_URL } from "./AuthContext";
import ReCAPTCHA from "react-google-recaptcha";
import PublicHeader from "./PublicHeader";

// --- Password strength helpers (shared rules) ---
function passwordIssues(pw = "", email = "") {
  const issues = [];
  const local = (email || "").split("@")[0] || "";
  if (pw.length < 12) issues.push("At least 12 characters");
  if (!/[a-z]/.test(pw)) issues.push("At least one lowercase letter");
  if (!/[A-Z]/.test(pw)) issues.push("At least one uppercase letter");
  if (!/[0-9]/.test(pw)) issues.push("At least one number");
  if (!/[^A-Za-z0-9\s]/.test(pw)) issues.push("At least one symbol");
  if (/\s/.test(pw)) issues.push("No spaces");
  if (local && pw.toLowerCase().includes(local.toLowerCase())) {
    issues.push("Must not contain your email username");
  }
  return issues;
}
const isStrongPassword = (pw, email) => passwordIssues(pw, email).length === 0;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

/** File types: order and labels exactly as requested */
const OTHER_LABEL = "Other (requires customization, additional charges may apply)";
const FILE_TYPES = [
  "DOCX (Microsoft Word)",
  "XLSX (Microsoft Excel)",
  "PPTX (Microsoft PowerPoint)",
  "IDML (Adobe InDesign)",
  "HTML",
  "XML",
  "JSON",
  "YML",
  "XLIFF",
  "SDLXLIFF (Trados)",
  "PDF (limited support)",
  OTHER_LABEL,
];

/** PEMT: re-ordered and default to Full Review */
const PEMT_OPTIONS = [
  { key: "full_review", label: "Full Human Review (additional charges apply)" },
  { key: "term_egregious", label: "Limited Review (terminology verification + egregious errors, additional charges apply)" },
  { key: "none", label: "No Review" },
];

/** Post-translation formatting */
const PT_FORMATTING = [
  { key: "pro_match_en", label: "Professional File Formatting (matches English formatting, additional charges apply)" },
  { key: "none", label: "Automated File Formatting Only (limited, may contain errors)" },
];

const STORAGE_KEY = "zab_workflow_builder_v1";
const DRAFT_ID_STORAGE_KEY = "zab_workflow_draft_id";
const DRAFT_TOKEN_STORAGE_KEY = "zab_workflow_draft_access_token";

export default function WorkflowBuilder({
  initialStep = 0,
  hideFinishLater = false,
  onBack,
}) {
  const { user, jwt, refreshUser, register, setAuthError } = useAuth();
  const [step, setStep] = useState(initialStep);
  // Language mappings (same contract as App.jsx)
  const [langCodeMap, setLangCodeMap] = useState({});
  const [langNameToCode, setLangNameToCode] = useState({});
  const languageList = useMemo(
    () => Object.keys(langNameToCode).sort((a, b) => a.localeCompare(b)),
    [langNameToCode]
  );
  
  const [data, setData] = useState({
    // Step 0: Your Details (mirror RegistrationForm fields)
    lead: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      password2: "",
      captchaToken: "",
      eulaAgreed: false,
      company: "", // kept for Finish Later modal (optional)
      website: "", // honeypot; legitimate users never see or fill this
    },
    
	// Step 1: Language pairs [{ source: "", target: "" }]
    pairs: [],
	
    // Step 2+
    fileTypes: [],
	otherFileType: "",
	otherSample: { file: null, filename: "" },  // local-only blob + name
    terminology: { enabled: false, file: null, filename: "", entries: [] },
    dataUsage: {
      shareMode: "no_share",   // "unspecified" | "no_share" | "public_ok"
      tmxFile: null,              // local-only File object
      tmxFilename: "",            // for display + draft persistence later
	},
	pemt: "full_review",        // default
    formatting: "pro_match_en", // default
  });

  // If a logged-in user opens the builder, skip Step 0 (account setup)
  useEffect(() => {
    if (!user) return;
    if (step !== 0) return;

    // Pre-fill lead details from the logged-in user where they are empty
    setData((prev) => {
      const lead = prev.lead || {};
      return {
        ...prev,
        lead: {
          ...lead,
          firstName: lead.firstName || user.first_name || "",
          lastName: lead.lastName || user.last_name || "",
          email: lead.email || user.email || "",
        },
      };
    });

    setStep(1);
  }, [user, step]);


  const [showExitModal, setShowExitModal] = useState(false);
  const [finishLaterCaptchaToken, setFinishLaterCaptchaToken] = useState("");
  const [finishLaterError, setFinishLaterError] = useState("");
  const [finishLaterSaving, setFinishLaterSaving] = useState(false);
  const [building, setBuilding] = useState(false);
  
  //Workflow builder save states
  const [draftId, setDraftId] = useState(() => {
    try {
      const raw = localStorage.getItem(DRAFT_ID_STORAGE_KEY);
      const parsed = raw ? Number(raw) : null;
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    } catch {
      return null;
    }
  });
  const [draftAccessToken, setDraftAccessToken] = useState(() => {
    try {
      return localStorage.getItem(DRAFT_TOKEN_STORAGE_KEY) || "";
    } catch {
      return "";
    }
  });
  const draftIdRef = React.useRef(draftId);
  const draftAccessTokenRef = React.useRef(draftAccessToken);
  const draftSavePromiseRef = React.useRef(null);
  const saveTimeoutRef = React.useRef(null);

  useEffect(() => {
    draftIdRef.current = draftId;
    draftAccessTokenRef.current = draftAccessToken;
  }, [draftId, draftAccessToken]);

  function rememberDraftAccess(json) {
    if (!json || !json.draft_id) return;
    const nextDraftId = Number(json.draft_id);
    const nextToken = json.draft_access_token || "";

    if (Number.isFinite(nextDraftId) && nextDraftId > 0) {
      draftIdRef.current = nextDraftId;
      setDraftId(nextDraftId);
      try {
        localStorage.setItem(DRAFT_ID_STORAGE_KEY, String(nextDraftId));
      } catch {}
    }

    if (nextToken) {
      draftAccessTokenRef.current = nextToken;
      setDraftAccessToken(nextToken);
      try {
        localStorage.setItem(DRAFT_TOKEN_STORAGE_KEY, nextToken);
      } catch {}
    }
  }

  function buildDraftPayload(options = {}) {
    const L = data.lead || {};
    const payload = {
      draft_id: draftIdRef.current || undefined,
      draft_access_token: draftAccessTokenRef.current || undefined,
      step,
      lead: {
        firstName: L.firstName || "",
        lastName: L.lastName || "",
        email: L.email || "",
        company: L.company || "",
        eulaAgreed: !!L.eulaAgreed,
        website: L.website || "",
      },
      website: L.website || "",
      pairs: data.pairs || [],
      file_types: data.fileTypes || [],
      other_file_type: data.otherFileType || "",
      other_sample_filename: data.otherSample?.filename || "",
      terminology: {
        enabled: !!data.terminology?.enabled,
        filename: data.terminology?.filename || "",
        confirmed: !!data.terminology?.confirmed,
        preview_pairs: Array.isArray(data.terminology?.previewPairs)
          ? data.terminology.previewPairs.slice(0, 200)
          : [],
        prompt: data.terminology?.prompt || "",
        entries: Array.isArray(data.terminology?.entries)
          ? data.terminology.entries
          : [],
      },
      data_usage: {
        share_mode: data.dataUsage?.shareMode || "no_share",
        tmx_filename: data.dataUsage?.tmxFilename || "",
        entries: Array.isArray(data.dataUsage?.entries)
          ? data.dataUsage.entries
          : [],
      },
      pemt: data.pemt,
      formatting: data.formatting,
    };

    if (options.captcha) {
      payload.captcha = options.captcha;
    }

    if (!payload.draft_id) delete payload.draft_id;
    if (!payload.draft_access_token) delete payload.draft_access_token;
    return payload;
  }

  async function saveDraft(payload) {
    const emailVal = String(payload?.lead?.email || "").trim();
    // Only persist drafts once we have an e-mail. This prevents a flood of anonymous draft rows
    // while the user is typing their name.
    if (!emailVal || emailVal.indexOf("@") === -1) {
      return null;
    }

    const payloadToSend = { ...payload };

    if (!payloadToSend.draft_id && draftIdRef.current) {
      payloadToSend.draft_id = draftIdRef.current;
    }
    if (!payloadToSend.draft_access_token && draftAccessTokenRef.current) {
      payloadToSend.draft_access_token = draftAccessTokenRef.current;
    }

    // If the very first draft creation is still in flight, wait for it so we do
    // not create duplicate drafts for the same e-mail while React state catches up.
    if (!payloadToSend.draft_id && !payloadToSend.draft_access_token && draftSavePromiseRef.current) {
      const previous = await draftSavePromiseRef.current.catch(() => null);
      if (previous?.draft_id) payloadToSend.draft_id = previous.draft_id;
      if (previous?.draft_access_token) payloadToSend.draft_access_token = previous.draft_access_token;
    }

    const savePromise = (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/workflow/draft`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payloadToSend),
          credentials: "include", // ok even if unauthenticated; harmless here
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          const detail =
            json?.detail ||
            `Unable to save the workflow draft (HTTP ${res.status}).`;
          throw new Error(
            typeof detail === "string" ? detail : JSON.stringify(detail)
          );
        }
        rememberDraftAccess(json);
        return json;
      } catch (err) {
        throw err;
      }
    })();

    draftSavePromiseRef.current = savePromise;
    try {
      return await savePromise;
    } finally {
      if (draftSavePromiseRef.current === savePromise) {
        draftSavePromiseRef.current = null;
      }
    }
  }

  function scheduleSave() {
    const emailVal = String((data.lead || {}).email || "").trim();
    // Only schedule draft saves once we have an e-mail.
    if (!emailVal || emailVal.indexOf("@") === -1) {
      return;
    }
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      void saveDraft(buildDraftPayload());
    }, 600);
  }
  
  // Load/save progress (omit file blob)
  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
		const restoredFileTypes = Array.isArray(parsed.fileTypes) ? parsed.fileTypes.filter((l) => FILE_TYPES.includes(l)) : [];
        setData((d) => ({
          ...d,
          ...parsed,
          terminology: {
            ...parsed.terminology,
            file: null,
            entries: Array.isArray(parsed.terminology?.entries)
              ? parsed.terminology.entries
              : [],
          },
          lead: {
            ...(parsed.lead || {}),
            captchaToken: "",  // force captcha to be re-confirmed this session
            eulaAgreed: false,  // force EULA to be re-confirmed this session
          },
          fileTypes: restoredFileTypes, // keep only current, valid labels
          otherFileType: parsed.otherFileType || "",
          otherSample: {
            file: null,
            filename:
              parsed.otherSample?.filename ||
              parsed.other_sample_filename ||
              "",
          },
        }));
      } catch {}
    }
  }, []);
  
  useEffect(() => {
    const toSave = {
      ...data,
      terminology: { ...data.terminology, file: null },
      otherSample: { ...data.otherSample, file: null }, // prevent file blob in storage
	  dataUsage: { ...data.dataUsage, tmxFile: null },  // prevent file blob in storage
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  }, [data]);

  // Fetch language mappings (same endpoint as App.jsx)
  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch(`${BACKEND_URL}/lang_codes`);
        const json = await resp.json();
        setLangCodeMap(json.code_to_name || {});
        setLangNameToCode(json.name_to_code || {});
      } catch {
        /* non-fatal */
      }
    })();
  }, []);

  React.useEffect(() => {
    // Only start saving once something exists (prevents empty rows)
    const hasAnyInput =
      (data.lead?.firstName || data.lead?.lastName || data.lead?.email || data.lead?.company) ||
      (Array.isArray(data.pairs) && data.pairs.some(p => p.source || p.target)) ||
      (Array.isArray(data.fileTypes) && data.fileTypes.length > 0) ||
      data.terminology?.enabled ||
      !!data.pemt || !!data.formatting;

    if (hasAnyInput) scheduleSave();
    /* eslint-disable-next-line */
  }, [data, step]);

  // Validation per step
  const canNext = useMemo(() => {
    if (step === 0) {
      const L = data.lead || {};
      // Only require basics + CAPTCHA + EULA here. Password is set later in ResetPassword.
      const hasBasics = !!L.firstName && !!L.lastName && !!L.email;
      const captchaOk =
        typeof L.captchaToken === "string" && L.captchaToken.length > 0;
      const eulaOk = !!L.eulaAgreed;
      return hasBasics && captchaOk && eulaOk;
    }
    if (step === 1) {
      const pairs = data.pairs || [];
      // complete = both sides selected and not equal
      const anyComplete = pairs.some((p) => !!p.source && !!p.target && p.source !== p.target);
      // partial = exactly one side selected
      const anyPartial = pairs.some((p) => (p.source && !p.target) || (!p.source && p.target));
      // allow next only if at least one complete pair AND no partial pairs
      return anyComplete && !anyPartial;
    }
    if (step === 2) {
      const hasAny = data.fileTypes.length > 0;
      const needsOtherText = data.fileTypes.includes(OTHER_LABEL);
      const hasOtherText = (data.otherFileType || "").trim().length > 0;
      return hasAny && (!needsOtherText || hasOtherText);
    }

    if (step === 3) {
      if (!data.terminology.enabled) return true;

      const validPairs = (data.pairs || []).filter(
        (p) => p.source && p.target && p.source !== p.target
      );

      const entries = Array.isArray(data.terminology?.entries)
        ? data.terminology.entries
        : [];

      // Legacy single-file path (kept for backwards compatibility)
      const legacyPairIndex =
        typeof data.terminology?.pairIndex === "number"
          ? data.terminology.pairIndex
          : null;
      const legacyHasFile = !!data.terminology?.filename;
      const legacyConfirmed =
        !!data.terminology?.confirmed &&
        Array.isArray(data.terminology?.previewPairs) &&
        data.terminology.previewPairs.length > 0;
      const legacyHasPairSelection =
        validPairs.length <= 1
          ? true
          : legacyPairIndex !== null &&
            legacyPairIndex >= 0 &&
            legacyPairIndex < validPairs.length;

      const legacyOk =
        legacyHasFile && legacyConfirmed && legacyHasPairSelection;

      // New multi-file path: at least one entry with a valid pair index + filename
      const multiOk = entries.some((entry) => {
        const idx =
          typeof entry.pairIndex === "number" ? entry.pairIndex : null;
        return (
          idx !== null &&
          idx >= 0 &&
          idx < validPairs.length &&
          !!entry.filename
        );
      });

      return legacyOk || multiOk;
    }

    if (step === 4) {
      return true;
    }

    if (step === 5) return !!data.pemt;
    if (step === 6) return !!data.formatting;
    return true;
  }, [step, data]);

  const next = () => setStep((s) => Math.min(s + 1, 7));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const abandon = () => {
    setFinishLaterCaptchaToken("");
    setFinishLaterError("");
    setShowExitModal(true);
  };

  const finish = () => {
    if (building) return;

    const lead = data.lead || {};

    // If the user is NOT authenticated, register them (name + email + CAPTCHA + EULA),
    // and then send them into the normal confirm-email flow. Password is set later
    // in ResetPassword, not collected here.
    if (!jwt) {
      if (!lead.firstName || !lead.lastName || !lead.email) {
        alert("Please fill out your name and e-mail before completing the workflow.");
        return;
      }
      if (!lead.captchaToken) {
        alert("Please complete the CAPTCHA before registering.");
        return;
      }
      if (!lead.eulaAgreed) {
        alert("You must agree to the End User License Agreement (EULA) to register.");
        return;
      }

      if (typeof setAuthError === "function") {
        setAuthError("");
      }

      setBuilding(true);

      if (typeof window !== "undefined") {
        window.scrollTo(0, 0);
      }

      // Reuse AuthContext.register; password is generated/enforced server-side
      // and the user sets a real password later via the confirmation + ResetPassword flow.
      register(
        lead.email,
        lead.firstName,
        lead.lastName,
        lead.captchaToken,
        /* eulaAgreed: */ true,
        lead.website || ""
      )
        .then((ok) => {
          // If registration failed (e.g., invalid CAPTCHA or duplicate email),
          // do not redirect; the AuthContext already set a helpful authError message.
          if (!ok) {
            return;
          }

          try {
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(DRAFT_ID_STORAGE_KEY);
            localStorage.removeItem(DRAFT_TOKEN_STORAGE_KEY);
          } catch {
            // non-fatal
          }

          // Go straight to the Confirm Email screen; that screen already explains
          // that a confirmation e-mail was sent, so we skip showing a separate alert here.
          if (typeof window !== "undefined") {
            window.location.href = "/confirm-email";
          }
        })
        .catch((err) => {
          console.error("Registration via workflow builder failed:", err);
          alert(
            "Registration failed. Please try again from the main Register page."
          );
        })
        .finally(() => {
          setBuilding(false);
        });

      // Important: do NOT call /workflow/complete when unauthenticated.
      return;
    }

    // If the user IS authenticated, keep the existing behavior:
    setBuilding(true);

    if (typeof window !== "undefined") {
      window.scrollTo(0, 0);
    }

    const payload = draftId
      ? { draft_id: draftId, ...(draftAccessToken ? { draft_access_token: draftAccessToken } : {}) }
      : {};

    axios
      .post(`${BACKEND_URL}/workflow/complete`, payload, {
        headers: { Authorization: `Bearer ${jwt}` },
      })
      .then((res) => {
        // Backend returns: { status: "ok", draft_id, uses_custom_workflow: true }
        const data = res.data || {};
        const ok = data.status === "ok";

        // Refresh the logged-in user so the header immediately shows "Custom workflow"
        if (typeof refreshUser === "function") {
          refreshUser();
        }

        if (ok) {
          alert("Your custom workflow has been saved and activated.");
        } else {
          alert("Workflow completed, but response was unexpected.");
        }
      })
      .catch((err) => {
        console.error("Failed to complete workflow:", err);
        const detail =
          err?.response?.data?.detail ||
          "Failed to complete workflow. Please make sure you are logged in.";
        alert(detail);
      })
      .finally(() => {
        setBuilding(false);
      });
  };


  // Small helpers for pairs
  const addPair = () =>
    setData((d) => ({
      ...d,
      pairs: [...(d.pairs || []), { source: "", target: "" }],
    }));

  const removePair = (idx) =>
    setData((d) => ({
      ...d,
      pairs: (d.pairs || []).filter((_, i) => i !== idx),
    }));

  const setPair = (idx, field, value) =>
    setData((d) => {
      const current = Array.isArray(d.pairs) ? d.pairs : [];
      const next = [...current];
      const existing = next[idx] || { source: "", target: "" };
      next[idx] = { ...existing, [field]: value };
      return { ...d, pairs: next };
    });

  // --- Finish Later: Save draft e-mail to self ---
  const handleSaveExitAndEmail = async () => {
    if (finishLaterSaving) return;

    const email = data?.lead?.email?.trim();
    setFinishLaterError("");

    if (!email) {
      setFinishLaterError(
        "Please enter your e-mail address so we can send you a link to resume your custom workflow later."
      );
      return;
    }

    if (!finishLaterCaptchaToken) {
      setFinishLaterError("Please complete the CAPTCHA before saving and exiting.");
      return;
    }

    setFinishLaterSaving(true);

    try {
      const saved = await saveDraft(
        buildDraftPayload({ captcha: finishLaterCaptchaToken })
      );
      const sendDraftId = saved?.draft_id || draftIdRef.current;
      const sendDraftToken =
        saved?.draft_access_token || draftAccessTokenRef.current;

      if (!sendDraftId || !sendDraftToken) {
        throw new Error(
          "The workflow draft could not be saved. Please try again."
        );
      }

      const response = await axios.post(
        `${BACKEND_URL}/workflow/drafts/send-email-self`,
        {
          email,
          draft_id: sendDraftId,
          draft_access_token: sendDraftToken,
          captcha: finishLaterCaptchaToken,
          website: data?.lead?.website || "",
        }
      );

      if (!response?.data?.ok) {
        throw new Error(
          "The workflow was saved, but the resume e-mail could not be requested."
        );
      }

      window.alert(
        "We've emailed you a link so you can resume your custom workflow later."
      );

      setShowExitModal(false);
      setFinishLaterCaptchaToken("");
      setFinishLaterError("");

      if (typeof window !== "undefined") {
        window.location.href = "/";
      }
    } catch (err) {
      console.error("Error saving or emailing workflow draft", err);
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "There was a problem saving your workflow and sending the e-mail. Please try again.";

      setFinishLaterError(
        typeof detail === "string" ? detail : JSON.stringify(detail)
      );

      // Keep the modal and workflow open so the user can retry and so the
      // browser Network response remains available for troubleshooting.
      setFinishLaterCaptchaToken("");
    } finally {
      setFinishLaterSaving(false);
    }
  };

  
  if (building) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center text-center px-6">
        <h1 className="text-3xl md:text-4xl font-extrabold mb-4">
          Building Custom Flow
        </h1>
        <p className="text-2xl md:text-3xl text-gray-600 mb-10">
          Please Wait...
        </p>
        <img
          src="/flow.gif"
          alt="Building custom flow"
          className="w-full max-w-xl h-auto"
        />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-white">
      {!user && <PublicHeader />}


      {onBack && (
        <div className="mx-auto max-w-3xl px-4 pt-4">
          <button
            type="button"
            onClick={onBack}
            className="mb-2 text-sm underline"
            style={{ color: "#00a7e1" }}
          >
            ← Back to Settings
          </button>
        </div>
      )}

      <div className="mx-auto max-w-3xl px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-semibold text-gray-900">Build Your Translation Workflow</h1>
          {!hideFinishLater && (
            <button
              onClick={abandon}
              className="text-sm underline"
              style={{ color: "#00a7e1" }}
            >
              Finish Later
            </button>
          )}
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-6">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="flex items-center">
              <div
                className="h-2 w-8 rounded-full"
                style={{ backgroundColor: i <= step ? "#00a7e1" : "#e5e7eb" /* gray-300 */ }}
              />
              {i < 7 && <div className="w-2" />}
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          {step === 0 && <DetailsStep data={data} setData={setData} />}
          {step === 1 && (
            <PairsStep
              data={data}
              setPair={setPair}
              addPair={addPair}
              removePair={removePair}
              languageList={languageList}
            />
          )}
          {step === 2 && <FileTypesStep data={data} setData={setData} />}
          {step === 3 && <TerminologyStep data={data} setData={setData} />}
          {step === 4 && (
            <DataUsageStep
              data={data}
              setData={setData}
              langNameToCode={langNameToCode}
            />
          )}
          {step === 5 && <PemtStep data={data} setData={setData} />}
          {step === 6 && <FormattingStep data={data} setData={setData} />}
		  {step === 7 && <SummaryStep data={data} />}

          <div className="mt-6 flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                // If on terminology step with a selected file but NOT confirmed, warn + optionally clear
                if (
                  step === 3 &&
                  data.terminology?.enabled &&
                  data.terminology?.filename &&
                 !data.terminology?.confirmed
                ) {
                  const ok = window.confirm(
                    "Your changes to Terminology Enforcement are not saved. Are you sure you want to go back?"
                  );
                  if (!ok) return;

                  // Clear terminology selection for this unsaved state
                  setData((d) => ({
                    ...d,
                    terminology: {
                      enabled: d.terminology?.enabled || false,
                      file: null,
                      filename: "",
                      confirmed: false,
                      previewPairs: [],
                      previewNote: "",
                      prompt: "",
                      pairIndex: undefined,
                      hasHeader: !!d.terminology?.hasHeader,
                    },
                  }));
                }

                setStep((s) => Math.max(s - 1, 0));
              }}
              disabled={step === 0}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm disabled:opacity-50"
            >
              Back
            </button>

            {step < 7 ? (
              <button
                type="button"
                onClick={() => { if (canNext) next(); }}
                disabled={!canNext}
                className="rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: "#00a7e1" }}
              >
                Continue
              </button>
            ) : (
              <button
                type="button"
                onClick={() => { if (canNext) finish(); }}
                disabled={!canNext}
                className="rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: "#94c83d" }}
              >
                Complete Custom Flow
              </button>
            )}
          </div>
        </div>
      </div>

      {showExitModal && (
        <LeadCaptureModal
          data={data}
          setData={setData}
          captchaToken={finishLaterCaptchaToken}
          onCaptchaChange={(token) => {
            setFinishLaterCaptchaToken(token || "");
            setFinishLaterError("");
          }}
          error={finishLaterError}
          saving={finishLaterSaving}
          onClose={() => {
            if (finishLaterSaving) return;
            setShowExitModal(false);
            setFinishLaterCaptchaToken("");
            setFinishLaterError("");
            if (typeof window !== "undefined") {
              window.location.href = "/";
            }
          }}
          onSave={handleSaveExitAndEmail}
        />
      )}

    </div>
  );
}

/* ================= Steps ================= */

function DetailsStep({ data, setData }) {
  const L = data.lead || {};
  const setLead = (patch) =>
    setData((d) => ({ ...d, lead: { ...d.lead, ...patch } }));

  return (
    <div>
      <h2 className="text-base font-semibold text-gray-900 mb-4">
        Your Details
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-700 mb-1">
            First Name
          </label>
          <input
            type="text"
            value={L.firstName}
            onChange={(e) => setLead({ firstName: e.target.value })}
            className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-700 mb-1">
            Last Name
          </label>
          <input
            type="text"
            value={L.lastName}
            onChange={(e) => setLead({ lastName: e.target.value })}
            className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm text-gray-700 mb-1">
            E-mail Address
          </label>
          <input
            type="email"
            value={L.email}
            onChange={(e) => setLead({ email: e.target.value })}
            className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
            autoComplete="username"
          />
        </div>

        <div
          aria-hidden="true"
          style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "hidden" }}
        >
          <label htmlFor="workflow-website">Website</label>
          <input
            id="workflow-website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={L.website || ""}
            onChange={(e) => setLead({ website: e.target.value })}
          />
        </div>

        <div className="md:col-span-2">
          <ReCAPTCHA
            sitekey={RECAPTCHA_SITE_KEY}
            onChange={(token) => setLead({ captchaToken: token || "" })}
            onExpired={() => setLead({ captchaToken: "" })}
            onErrored={() => setLead({ captchaToken: "" })}
          />
        </div>

        <div className="md:col-span-2 flex items-start gap-3">
          <input
            id="eula"
            type="checkbox"
            className="w-5 h-5"
            checked={!!L.eulaAgreed}
            onChange={(e) => setLead({ eulaAgreed: e.target.checked })}
          />
          <label htmlFor="eula" className="text-sm">
            I agree to the{" "}
            <a
              className="underline"
              style={{ color: "#00a7e1" }}
              href="https://app.termly.io/policy-viewer/policy.html?policyUUID=d083b4a4-7dea-4898-b51e-e34e14d5a432"
              target="_blank"
              rel="noreferrer"
            >
              End User License Agreement
            </a>
            .
          </label>
        </div>
      </div>
    </div>
  );
}

function PairsStep({ data, setPair, addPair, removePair, languageList }) {
  const [newSource, setNewSource] = useState("");
  const [newTarget, setNewTarget] = useState("");

  const pairs = Array.isArray(data.pairs) ? data.pairs : [];
  const anyComplete = pairs.some(
    (p) => !!p.source && !!p.target && p.source !== p.target
  );

  const handleAdd = () => {
    const src = newSource.trim();
    const tgt = newTarget.trim();
    if (!src || !tgt) return;
    if (src === tgt) return;

    const idx = pairs.length;
    setPair(idx, "source", src);
    setPair(idx, "target", tgt);
    setNewSource("");
    setNewTarget("");
  };

  const newNeedsSource = !newSource && !!newTarget;
  const newNeedsTarget = !!newSource && !newTarget;

  return (
    <div>
      <h2 className="text-base font-semibold text-gray-900 mb-1">
        Language Pairs
      </h2>
      <p className="text-xs text-gray-600 mb-3">
        Select one or more language pairs. Existing pairs are shown below; use the Add
        row to include additional pairs.
      </p>

      {/* Existing committed language pairs (read-only) */}
      {pairs.length > 0 && (
        <div className="space-y-2 mb-4">
          {pairs.map((p, idx) => {
            if (!p.source || !p.target) return null;
            return (
              <div
                key={`${p.source}-${p.target}-${idx}`}
                className="flex items-center gap-3 text-sm text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg px-3 py-2 inline-flex transition-colors"
              >
                <span className="font-semibold">
                  {p.source} <span style={{ color: "#00a7e1" }}>{"\u2192"}</span> {p.target}
                </span>
                <button
                  type="button"
                  onClick={() => removePair(idx)}
                  className="rounded-xl border border-gray-300 px-2 py-1 text-xs md:text-sm"
                >
                  Remove
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* New language pair row */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-start">
        <div className="md:col-span-2">
          <label className="block text-sm text-gray-700 mb-1">Source Language</label>
          <select
            className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
            value={newSource}
            onChange={(e) => setNewSource(e.target.value)}
          >
            <option value="">Select source…</option>
            {languageList.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          {newNeedsSource && (
            <div className="text-xs text-red-700 mt-1" aria-live="polite">
              Please select a source language.
            </div>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm text-gray-700 mb-1">Target Language</label>
          <select
            className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
            value={newTarget}
            onChange={(e) => setNewTarget(e.target.value)}
          >
            <option value="">Select target…</option>
            {languageList
              .filter((name) => name !== newSource)
              .map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
          </select>
          {newNeedsTarget && (
            <div className="text-xs text-red-700 mt-1" aria-live="polite">
              Please select a target language.
            </div>
          )}
        </div>

        <div className="md:col-span-1 flex justify-end md:justify-start mt-6">
          <button
            type="button"
            onClick={handleAdd}
            disabled={!newSource || !newTarget || newSource === newTarget}
            className="rounded-xl px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: "#00a7e1" }}
          >
            Add
          </button>
        </div>
      </div>

      {!anyComplete && (
        <p className="text-xs text-red-700 mt-2">
          Please add at least one language pair (source and target) to
          continue.
        </p>
      )}
    </div>
  );
}

function SampleUpload({ data, setData }) {
  const [err, setErr] = React.useState("");
  const fileInputRef = React.useRef(null);

  // Block obviously dangerous extensions; keep this conservative on the client.
  const bannedExts = [
    ".exe",".msi",".dll",".scr",".com",".bat",".cmd",".ps1",".psm1",
    ".vbs",".js",".jar",".sh",".apk",".appimage",".iso",".dmg"
  ];
  const MAX_MB = 20;

  const onChange = (e) => {
    setErr("");
    const f = e.target.files?.[0];
    if (!f) {
      setData((d) => ({ ...d, otherSample: { file: null, filename: "" } }));
      return;
    }
    const name = f.name || "";
    const lower = name.toLowerCase();
    if (bannedExts.some((ext) => lower.endsWith(ext))) {
      setErr("This file type is not allowed for security reasons.");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      setErr(`File too large. Please keep under ${MAX_MB} MB.`);
      e.target.value = "";
      return;
    }
    setData((d) => ({ ...d, otherSample: { file: f, filename: name } }));
  };

  return (
    <>
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="rounded-xl px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
        style={{ backgroundColor: "#00a7e1" }}
      >
        Choose Sample File
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".zip,.txt,.csv,.docx,.xlsx,.pptx,.idml,.html,.xml,.json,.yml,.yaml,.xlf,.xliff,.sdlxliff,.pdf"
        onChange={onChange}
        className="hidden"
      />
      {err && (
        <div className="text-xs text-red-700 mt-1" aria-live="polite">
          {err}
        </div>
      )}
    </>
  );
}

function FileTypesStep({ data, setData }) {
  const toggle = (label) =>
    setData((d) => ({
      ...d,
      fileTypes: d.fileTypes.includes(label)
        ? d.fileTypes.filter((x) => x !== label)
        : [...d.fileTypes, label],
    }));

  const otherSelected = data.fileTypes.includes(OTHER_LABEL);
  const otherEmpty = !((data.otherFileType || "").trim().length);

    const standardFileTypes = FILE_TYPES.filter((label) => label !== OTHER_LABEL);
    const allStandardSelected = standardFileTypes.every((label) =>
      data.fileTypes.includes(label)
    );

    return (
    <div>
      <h2 className="text-base font-semibold text-gray-900 mb-4">File Types</h2>

      <button
        type="button"
        onClick={() =>
          setData((d) => {
            const currentFileTypes = Array.isArray(d.fileTypes) ? d.fileTypes : [];
            const allSelected = standardFileTypes.every((label) =>
              currentFileTypes.includes(label)
            );
            return {
              ...d,
              fileTypes: allSelected
                ? currentFileTypes.filter((label) => label === OTHER_LABEL)
                : Array.from(new Set([...currentFileTypes, ...standardFileTypes])),
            };
          })
        }
        className="mb-4 rounded-xl px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
        style={{ backgroundColor: "#00a7e1" }}
      >
        {allStandardSelected ? "Unselect All" : "Select All"}
      </button>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {FILE_TYPES
          .filter((label) => label !== OTHER_LABEL) // render all standard types in the grid
          .map((label) => (
            <label key={label} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={data.fileTypes.includes(label)}
                onChange={() =>
                  setData((d) => ({
                    ...d,
                    fileTypes: d.fileTypes.includes(label)
                      ? d.fileTypes.filter((x) => x !== label)
                      : [...d.fileTypes, label],
                  }))
                }
              />
              {label}
            </label>
          ))}
      </div>

      {/* "Other" on its own line, just above "Please specify:" */}
      <div className="mt-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={data.fileTypes.includes(OTHER_LABEL)}
            onChange={() =>
              setData((d) => {
                const checked = !d.fileTypes.includes(OTHER_LABEL);
                const next = {
                  ...d,
                  fileTypes: checked
                    ? [...d.fileTypes, OTHER_LABEL]
                    : d.fileTypes.filter((x) => x !== OTHER_LABEL),
                };
                return next;
              })
            }
          />
          {OTHER_LABEL}
        </label>
      </div>

      {/* “Other” free-text + optional sample upload only when selected */}
      {otherSelected && (
        <div className="mt-3">

          <label className="block text-sm text-gray-700 mb-1">
            Please specify file type and describe file requirements:
          </label>
          <textarea
            value={data.otherFileType}
            onChange={(e) =>
              setData((d) => ({ ...d, otherFileType: e.target.value }))
            }
            className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm resize-y overflow-y-auto"
            placeholder="e.g., Markdown, DITA, LaTeX, custom XML, etc."
            rows={4}
            maxLength={250}
          />
          <div className="mt-1 text-xs text-gray-500">
            {data.otherFileType.length}/250 characters
          </div>
          {otherEmpty && (
            <p className="text-xs text-red-700 mt-1" aria-live="polite">
              Please enter custom file type and description.
            </p>
          )}

          <div className="mt-4">
            <label className="block text-sm text-gray-700 mb-1">
              Please select a sample file if available (optional)
            </label>
            <SampleUpload data={data} setData={setData} />
            {data.otherSample?.filename && (
              <div className="mt-2 flex items-center gap-3 text-sm text-gray-700">
                <span>
                  Selected:{" "}
                  <span className="font-semibold">{data.otherSample.filename}</span>
                </span>
                <button
                 type="button"
                  onClick={() =>
                    setData((d) => ({ ...d, otherSample: { file: null, filename: "" } }))
                  }
                  className="text-sm text-red-600 underline"
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        </div>
      )}


      {/* Existing “select at least one” message */}
      {data.fileTypes.length === 0 && (
        <p className="text-xs text-red-700 mt-2">
          Select at least one file type to continue.
        </p>
      )}
    </div>
  );
}

// --- Terminology preview helpers ---
// Parse CSV/TXT quickly; XLSX via dynamic import('xlsx') (only when needed).

function detectDelimiter(sample) {
  // Prefer tab, then comma, then semicolon
  if (/\t/.test(sample)) return "\t";
  if (/,[^\n]/.test(sample)) return ",";
  if (/;[^\n]/.test(sample)) return ";";
  return ","; // default
}

function parseCSVText(text, maxRows = 200) {
  const delim = detectDelimiter(text.slice(0, 10_000));
  const rows = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0)
    .slice(0, maxRows + 2) // +2 to help heuristics
    .map((l) => l.split(delim).map((c) => c.trim()));

  // If more than 2 cols, choose two with max non-empty values.
  if (rows.length && rows[0].length > 2) {
    const cols = rows[0].length;
    let bestPair = [0, 1], bestScore = -1;
    for (let i = 0; i < cols; i++) {
      for (let j = i + 1; j < cols; j++) {
        let score = 0;
        for (const r of rows) {
          if ((r[i] || "").length) score++;
          if ((r[j] || "").length) score++;
        }
        if (score > bestScore) {
          bestScore = score;
          bestPair = [i, j];
        }
      }
    }
    return rows.map((r) => [r[bestPair[0]] || "", r[bestPair[1]] || ""]);
  }
  return rows.map((r) => [r[0] || "", r[1] || ""]);
}

function parseTXTText(text, maxPairs = 200) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const pairs = [];

  // 1) Try tab-separated lines
  const anyTab = lines.some((l) => l.includes("\t"));
  if (anyTab) {
    for (const l of lines) {
      const [a, b] = l.split("\t");
      if (a && b) pairs.push([a.trim(), b.trim()]);
      if (pairs.length >= maxPairs) break;
    }
    if (pairs.length) return pairs;
  }

  // 2) Try comma-separated lines
  const anyComma = lines.some((l) => l.includes(","));
  if (anyComma) {
    for (const l of lines) {
      const parts = l.split(",").map((x) => x.trim());
      if (parts.length >= 2 && parts[0] && parts[1]) {
        pairs.push([parts[0], parts[1]]);
        if (pairs.length >= maxPairs) break;
      }
    }
    if (pairs.length) return pairs;
  }

  // 3) Try alternating lines: source on odd, target on even
  for (let i = 0; i + 1 < lines.length && pairs.length < maxPairs; i += 2) {
    if (lines[i] && lines[i + 1]) pairs.push([lines[i], lines[i + 1]]);
  }
  return pairs;
}

async function parseXLSXFile(file, maxRows = 200) {
  const XLSX = await import(/* webpackChunkName: "xlsx" */ "xlsx"); // dynamic, only when needed
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const sheetName = wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true });

  const trimmed = rows
    .filter((r) => Array.isArray(r))
    .map((r) => r.map((c) => String(c ?? "").trim()))
    .filter((r) => r.some((c) => c.length))
    .slice(0, maxRows + 2);

  if (!trimmed.length) return [];

  if (trimmed[0].length > 2) {
    // Pick two densest columns
    const cols = trimmed[0].length;
    let best = [0, 1], bestScore = -1;
    for (let i = 0; i < cols; i++) {
      for (let j = i + 1; j < cols; j++) {
        let score = 0;
        for (const r of trimmed) {
          if ((r[i] || "").length) score++;
          if ((r[j] || "").length) score++;
        }
        if (score > bestScore) {
          bestScore = score;
          best = [i, j];
        }
      }
    }
    return trimmed.map((r) => [r[best[0]] || "", r[best[1]] || ""]);
  }
  return trimmed.map((r) => [r[0] || "", r[1] || ""]);
}

async function buildTerminologyPreview(file) {
  const name = (file?.name || "").toLowerCase();
  if (!file) return { pairs: [], note: "" };

  if (name.endsWith(".csv")) {
    const text = await file.text();
    return { pairs: parseCSVText(text), note: "Detected CSV (2 columns preferred)." };
  }
  if (name.endsWith(".txt")) {
    const text = await file.text();
    return {
      pairs: parseTXTText(text),
      note: "Detected TXT (tab/comma-separated, or alternating lines).",
    };
  }
  if (name.endsWith(".xlsx")) {
    try {
      const pairs = await parseXLSXFile(file);
      return { pairs, note: "Detected XLSX (first sheet, first two relevant columns)." };
    } catch {
      return { pairs: [], note: "Could not parse XLSX without a proper table." };
    }
  }
  return { pairs: [], note: "Unsupported file type for preview." };
}

function TerminologyStep({ data, setData }) {
  const [preview, setPreview] = React.useState({
    pairs: Array.isArray(data.terminology?.previewPairs)
      ? data.terminology.previewPairs
      : [],
    note: data.terminology?.previewNote || "",
  });
  const [pending, setPending] = React.useState(false);
  const [confirm, setConfirm] = React.useState(
    !!data.terminology?.confirmed
  );
  const [err, setErr] = React.useState("");
  const [hasHeader, setHasHeader] = React.useState(
    !!data.terminology?.hasHeader
  );
  const fileInputRef = React.useRef(null);

  const enabled = !!data.terminology.enabled;
  const filename = data.terminology.filename || "";
  const emailForDraft = (data.lead?.email || "").trim();

  // Valid language pairs from step 1
  const validPairs = (data.pairs || []).filter(
    (p) => p.source && p.target && p.target !== p.source
  );

  // Multi-file entries already added to this workflow
  const entries = Array.isArray(data.terminology?.entries)
    ? data.terminology.entries
    : [];

  const initialPairIndex =
    typeof data.terminology?.pairIndex === "number"
      ? data.terminology.pairIndex
      : validPairs.length === 1
      ? 0
      : null;
  const [selectedPairIndex, setSelectedPairIndex] = React.useState(
    initialPairIndex
  );

  // --- used vs available pairs for this wizard step ---
  const usedPairIndices = new Set(
    entries
      .map((e) =>
        typeof e.pairIndex === "number" ? e.pairIndex : null
      )
      .filter((idx) => idx !== null)
  );

  const availableIndices = validPairs
    .map((_, idx) => idx)
    .filter((idx) => !usedPairIndices.has(idx));

  const hasUnusedPairs = availableIndices.length > 0;

  // If the currently selected index is already used (or null), fall back
  // to the first available index, if any.
  const effectiveSelectedIndex =
    selectedPairIndex !== null && availableIndices.includes(selectedPairIndex)
      ? selectedPairIndex
      : hasUnusedPairs
      ? availableIndices[0]
      : null;

  const selectedPair =
    effectiveSelectedIndex !== null
      ? validPairs[effectiveSelectedIndex]
      : null;

  const uploadButtonLabel =
    entries.length > 0 ? "Add Additional Terminology File" : "Choose Terminology File";

  // Whenever effectiveSelectedIndex or hasHeader changes, persist minimal metadata
  React.useEffect(() => {
    setData((d) => ({
      ...d,
      terminology: {
        ...d.terminology,
        pairIndex:
          effectiveSelectedIndex !== null && effectiveSelectedIndex >= 0
            ? effectiveSelectedIndex
            : d.terminology?.pairIndex,
        hasHeader,
      },
    }));
    // eslint-disable-next-line
  }, [effectiveSelectedIndex, hasHeader]);

  // Build prompt using effective pairs (skip header row if flagged)
  const buildPrompt = React.useCallback(() => {
    const rawPairs = preview.pairs || [];
    const effectivePairs = hasHeader ? rawPairs.slice(1) : rawPairs;
    const examples = effectivePairs.slice(0, 20);
    const lines = examples
      .filter(([s, t]) => s && t)
      .map(([s, t]) => `- "${s}" → "${t}"`);

    const pairLine = selectedPair
      ? `Source language: ${selectedPair.source}\nTarget language: ${selectedPair.target}\n`
      : "";

    return [
      pairLine,
      "Use the following terminology consistently in all translations.",
      "If the exact source term appears, prefer its mapped target term.",
      "If capitalization or pluralization varies, apply the closest matching mapping.",
      "Terminology mappings:",
      ...lines,
    ]
      .filter(Boolean)
      .join("\n");
  }, [preview.pairs, hasHeader, selectedPair]);

  // Persist filename + preview snippet + prompt into draft once user confirms
  React.useEffect(() => {
    if (!enabled || !confirm || !preview.pairs.length) return;
    setData((d) => ({
      ...d,
      terminology: {
        ...d.terminology,
        confirmed: true,
        previewPairs: preview.pairs.slice(0, 200),
        previewNote: preview.note || "",
        prompt: buildPrompt(),
      },
    }));
    // eslint-disable-next-line
  }, [confirm, preview.pairs, preview.note, enabled, buildPrompt]);

  const onFile = async (file) => {
    setErr("");
    setConfirm(false);
    setPreview({ pairs: [], note: "" });

    if (!file) {
      setData((d) => ({
        ...d,
        terminology: {
          ...d.terminology,
          file: null,
          filename: "",
          confirmed: false,
          previewPairs: [],
          previewNote: "",
          prompt: "",
        },
      }));
      return;
    }

    const allowed = [".csv", ".xlsx", ".txt"];
    const lower = (file.name || "").toLowerCase();
    if (!allowed.some((ext) => lower.endsWith(ext))) {
      setErr("Unsupported file type. Please upload a CSV, XLSX, or TXT.");
      return;
    }

    // Store file + filename in local state for preview
    setData((d) => ({
      ...d,
      terminology: {
        ...d.terminology,
        file,
        filename: file.name || "",
        confirmed: false,
        previewPairs: [],
        previewNote: "",
        prompt: "",
      },
    }));

    // Fire-and-forget upload to backend so the terminology file is stored in S3
    (async () => {
      try {
        const formData = new FormData();
        formData.append("file", file);
        if (emailForDraft) {
          formData.append("email", emailForDraft);
        }
        if (draftIdRef.current) {
          formData.append("draft_id", String(draftIdRef.current));
        }
        if (draftAccessTokenRef.current) {
          formData.append("draft_access_token", draftAccessTokenRef.current);
        }
        const resp = await fetch(
          `${BACKEND_URL}/workflow/terminology-file`,
          {
            method: "POST",
            body: formData,
            credentials: "include",
          }
        );
        const json = await resp.json().catch(() => ({}));
        if (resp.ok) {
          rememberDraftAccess(json);
        } else {
          console.error(
            "Failed to upload terminology file",
            resp.status
          );
        }
      } catch (e) {
        console.error("Error uploading terminology file", e);
      }
    })();

    try {
      setPending(true);
      const { pairs, note } = await buildTerminologyPreview(file);
      const cleaned = (pairs || []).filter(([a, b]) => a || b);
      setPreview({ pairs: cleaned, note });
      setData((d) => ({
        ...d,
        terminology: {
          ...d.terminology,
          previewPairs: cleaned,
          previewNote: note || "",
        },
      }));
      if (!cleaned.length) {
        setErr("No term pairs detected. Please check your file layout.");
      }
    } catch (e) {
      setErr("Could not parse the file for preview.");
    } finally {
      setPending(false);
    }
  };

  // Effective rows for preview (skip header row if user says so)
  const effectiveRows = hasHeader ? preview.pairs.slice(1) : preview.pairs;

  const clearTerminologyFile = () => {
    setErr("");
    setPending(false);
    setPreview({ pairs: [], note: "" });
    setConfirm(false);
    setHasHeader(false);
    // If there is exactly one unused pair, keep that; otherwise, clear selection
    const nextIndex =
      availableIndices.length === 1 ? availableIndices[0] : null;
    setSelectedPairIndex(nextIndex);

    setData((d) => ({
      ...d,
      terminology: {
        ...d.terminology,
        file: null,
        filename: "",
        confirmed: false,
        previewPairs: [],
        previewNote: "",
        prompt: "",
        pairIndex: undefined,
      },
    }));
  };

  const addCurrentFileToEntries = () => {
    if (!enabled) return;

    if (!filename) {
      setErr("Please choose a terminology file first.");
      return;
    }
    if (!selectedPair) {
      setErr(
        "Please select which language pair this terminology applies to."
      );
      return;
    }
    if (!effectiveRows.length) {
      setErr(
        "No term pairs detected. Please check your file layout before adding."
      );
      return;
    }
    if (!confirm) {
      setErr(
        "Please confirm the columns correctly represent source and target terms before adding this file."
      );
      return;
    }

    const prompt = buildPrompt();

    setData((d) => {
      const existingEntries = Array.isArray(d.terminology?.entries)
        ? d.terminology.entries
        : [];

      const nextEntries = [
        ...existingEntries,
        {
          filename: d.terminology.filename,
          previewPairs: preview.pairs.slice(0, 200),
          previewNote: preview.note || "",
          hasHeader,
          // Use the effective index so we never assign to an already-used pair
          pairIndex: effectiveSelectedIndex,
          prompt,
        },
      ];

      return {
        ...d,
        terminology: {
          ...d.terminology,
          entries: nextEntries,
          // Clear staged/current file
          file: null,
          filename: "",
          confirmed: false,
          previewPairs: [],
          previewNote: "",
          prompt: "",
          pairIndex: undefined,
        },
      };
    });

    // Reset staging state; recompute selection based on remaining unused pairs
    setPreview({ pairs: [], note: "" });
    setConfirm(false);
    setHasHeader(false);

    const remainingUnused = availableIndices.filter(
      (idx) => idx !== effectiveSelectedIndex
    );
    const nextIndex =
      remainingUnused.length === 1
        ? remainingUnused[0]
        : remainingUnused.length > 1
        ? remainingUnused[0]
        : null;
    setSelectedPairIndex(nextIndex);

    setErr("");
  };

  return (
    <div>
      <h2 className="text-base font-semibold text-gray-900 mb-4">
        Terminology Enforcement
      </h2>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => {
            const enableNext = e.target.checked;
            if (!enableNext) {
              // Clear everything when disabling terminology
              setPreview({ pairs: [], note: "" });
              setConfirm(false);
              setSelectedPairIndex(
                validPairs.length === 1 ? 0 : null
              );
              setData((d) => ({
                ...d,
                terminology: {
                  enabled: false,
                  file: null,
                  filename: "",
                  confirmed: false,
                  previewPairs: [],
                  previewNote: "",
                  prompt: "",
                  pairIndex: undefined,
                  hasHeader: d.terminology?.hasHeader || false,
                  entries: [],
                },
              }));
            } else {
              setData((d) => ({
                ...d,
                terminology: { ...d.terminology, enabled: true },
              }));
            }
          }}
        />
        Enable Terminology Enforcement
      </label>

      {enabled && (
        <div className="mt-4">
          <p className="text-sm text-gray-600 mb-2">
            <b>
              Please upload a CSV, XLSX, or TXT with only one source and one
              target language per file.
            </b>{" "}
            We’ll then apply your terms across all translations. You can add only{" "}
            <b>one file per language pair</b>.
          </p>

          {/* Only show upload button while there are still unused language pairs */}
          {hasUnusedPairs && (
            <>
              {/* Custom file picker to avoid "No file chosen" UX */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="rounded-xl px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
                style={{ backgroundColor: "#00a7e1" }}
              >
                {uploadButtonLabel}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.txt"
                onChange={(e) => onFile(e.target.files?.[0] || null)}
                className="hidden"
              />
            </>
          )}

          {/* Currently staged file (not yet added to entries) */}
          {filename && (
            <div className="text-sm text-gray-700 mt-2">
              <div>Selected (not yet added to the list):</div>
              <div className="flex items-center gap-3">
                <div className="font-semibold">
                  {filename}
                  {selectedPair && (
                    <>
                      {", "}
                      {selectedPair.source} {"\u2192"} {selectedPair.target}
                    </>
                  )}
                </div>
                <button
                  type="button"
                  onClick={clearTerminologyFile}
                  className="text-sm text-red-600 underline"
                >
                  Remove
                </button>
              </div>
            </div>
          )}

          {/* Language pair selection if multiple pairs exist and a file is staged.
              Only show radio buttons for pairs that do NOT already have a terminology file. */}
          {enabled && filename && validPairs.length > 1 && hasUnusedPairs && (
            <div className="mt-3 text-sm text-gray-700">
              <div className="mb-1 font-semibold">
                Which language pair does this terminology apply to?
              </div>
              {validPairs.map((p, idx) => {
                if (usedPairIndices.has(idx)) return null;
                return (
                  <label
                    key={`${p.source}-${p.target}-${idx}`}
                    className="flex items-center gap-2 mb-1"
                  >
                    <input
                      type="radio"
                      name="term_pair"
                      checked={effectiveSelectedIndex === idx}
                      onChange={() => setSelectedPairIndex(idx)}
                    />
                    {p.source} {"\u2192"} {p.target}
                  </label>
                );
              })}
            </div>
          )}

          {pending && (
            <div
              className="mt-2 flex items-center text-sm"
              style={{ color: "#00a7e1" }}
            >
              <span
                className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent align-[-0.125em]"
                aria-hidden="true"
              />
              <span>Parsing file for preview…</span>
            </div>
          )}
          {err && (
            <div
              className="text-sm text-red-700 mt-2"
              aria-live="polite"
            >
              {err}
            </div>
          )}

          {/* Preview table for the currently staged file */}
          {effectiveRows.length > 0 && !pending && (
            <div className="mt-4">
              {preview.note && (
                <div className="text-sm text-gray-600 mb-2">
                  {preview.note}
                </div>
              )}

              <p className="mt-2 text-sm font-semibold text-gray-700">
                <b>Preview:</b>
              </p>

              <div className="overflow-auto rounded border border-gray-200 mt-3">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">
                        Source Term
                      </th>
                      <th className="text-left px-3 py-2 font-semibold">
                        Target Term
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {effectiveRows.slice(0, 10).map(([src, tgt], i) => (
                      <tr
                        key={i}
                        className={i % 2 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="px-3 py-2">{src}</td>
                        <td className="px-3 py-2">{tgt}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Header row toggle – now below the preview table */}
              <label className="mt-3 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={hasHeader}
                  onChange={(e) => setHasHeader(e.target.checked)}
                />
                <span>This file contains a header row.</span>
              </label>

              {/* Confirmation for staged file */}
              <label className="mt-3 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={confirm}
                  onChange={(e) =>
                    setConfirm(e.target.checked)
                  }
                />
                <span>
                  I confirm these columns correctly represent{" "}
                  <span className="font-semibold">source</span> and{" "}
                  <span className="font-semibold">target</span> terms.
                </span>
              </label>

              {/* Commit staged file into entries */}
              <button
                type="button"
                onClick={addCurrentFileToEntries}
                disabled={!filename}
                className={`mt-3 rounded-xl px-3 py-2 text-sm font-semibold text-white ${!filename ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"}`}
                style={{ backgroundColor: "#00a7e1" }}
              >
                Commit Terminology for Language Pair
              </button>

              <p className="mt-2 text-xs text-gray-600">
                Terminology won&apos;t preview properly? Please see our{" "}
                <a
                  href="https://zab-ai.helpscoutdocs.com/article/13-terminology-enforcement-how-to-format-your-terminology-file-txt-csv-xlsx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                  style={{ color: "#00a7e1" }}
                >
                  knowledge base article
                </a>{" "}
                for additional help or contact us at{" "}
                <a
                  href="mailto:support@zabtranslation.com"
                  className="underline"
                  style={{ color: "#00a7e1" }}
                >
                  support@zabtranslation.com
                </a>
                .
              </p>
            </div>
          )}

          {/* List of all entries added to this workflow */}
          {entries.length > 0 && (
            <div className="mt-6 text-sm text-gray-700">
              <div className="font-semibold mb-1">
                Terminology files added to this workflow:
              </div>
              <ul className="space-y-1">
                {entries.map((entry, idx) => {
                  const pair =
                    typeof entry.pairIndex === "number" &&
                    validPairs[entry.pairIndex]
                      ? validPairs[entry.pairIndex]
                      : null;
                  return (
                    <li
                      key={`${entry.filename}-${idx}`}
                      className="flex items-center gap-3"
                    >
                      <span>
                        {entry.filename}
                        {pair && (
                          <>
                            {", "}
                            {pair.source} {"\u2192"} {pair.target}
                          </>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setData((d) => ({
                            ...d,
                            terminology: {
                              ...d.terminology,
                              entries: (d.terminology.entries || []).filter(
                                (_, i) => i !== idx
                              ),
                            },
                          }))
                        }
                        className="text-xs text-red-600 underline"
                      >
                        Remove
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function DataUsageStep({ data, setData, langNameToCode }) {
  const du = data.dataUsage || {};
  const setDataUsage = (patch) =>
    setData((d) => ({
      ...d,
      dataUsage: { ...d.dataUsage, ...patch },
    }));

  const shareMode = du.shareMode || "no_share";

  // Valid language pairs from Step 1
  const validPairs = (data.pairs || []).filter(
    (p) => p.source && p.target && p.target !== p.source
  );

  const entries = Array.isArray(du.entries) ? du.entries : [];

  // Track which pair indices already have a TMX file
  const usedPairIndices = new Set(
    entries
      .map((e) => (typeof e.pairIndex === "number" ? e.pairIndex : null))
      .filter((idx) => idx !== null)
  );

  const availableIndices = validPairs
    .map((_, idx) => idx)
    .filter((idx) => !usedPairIndices.has(idx));

  const hasUnusedPairs = availableIndices.length > 0;

  // Initial selection
  const initialPairIndex = validPairs.length === 1 ? 0 : null;
  const [selectedPairIndex, setSelectedPairIndex] = React.useState(initialPairIndex);
  const [lastUploadedTmxKey, setLastUploadedTmxKey] = React.useState("");
  const [overridePending, setOverridePending] = React.useState(false);

  const effectiveSelectedIndex =
    selectedPairIndex !== null && availableIndices.includes(selectedPairIndex)
      ? selectedPairIndex
      : hasUnusedPairs
      ? availableIndices[0]
      : null;

  const selectedPair =
    effectiveSelectedIndex !== null ? validPairs[effectiveSelectedIndex] : null;

  const fileInputRef = React.useRef(null);

  // --- Helpers to detect TMX languages client-side ---
  const normalizeCode = (codeOrName = "") => {
    // If it's a display name, map to code; else treat input as code.
    const asCode = langNameToCode?.[codeOrName] || codeOrName;
    const clean = String(asCode || "").toLowerCase().replace(/_/g, "-");
    // Use primary subtag for loose matching (en-US -> en)
    return clean.split("-")[0];
  };

  const detectTMXLangs = async (file) => {
    const text = await file.text();
    const langs = new Set();
    const re = /<tuv\b[^>]*?\bxml:lang\s*=\s*["']([^"']+)["']/gi;
    let m;
    while ((m = re.exec(text))) {
      const lang = String(m[1] || "").trim();
      if (lang) {
        const clean = lang.toLowerCase().replace(/_/g, "-").split("-")[0];
        langs.add(clean);
        if (langs.size > 3) break; // quick escape
      }
    }
    return Array.from(langs);
  };

  const onTmxFile = async (file) => {
    // reset the override flag each time a new file is staged
    setOverridePending(false);

    if (!file) {
      setDataUsage({ tmxFile: null, tmxFilename: "" });
      setLastUploadedTmxKey("");
      return;
    }

    const name = file.name || "";
    if (!name.toLowerCase().endsWith(".tmx")) {
      alert("Please upload a TMX file (.tmx).");
      return;
    }

    if (!validPairs.length) {
      alert("Please select at least one language pair in Step 1 before adding TMX files.");
      return;
    }

    // Detect languages inside the TMX before we upload
    try {
      const langs = await detectTMXLangs(file); // e.g., ["en","de"]
      if (langs.length === 0) {
        if (!window.confirm("No languages detected in this TMX. Continue anyway?")) {
          return;
        }
        // If user continues, treat this like a mismatch override so the backend
        // pairs the first two segments per <tu>.
        setOverridePending(true);
      } else if (langs.length > 2) {
        alert("This TMX file contains more than two languages. Please provide a TMX file with only one source and one target language.");
        return;
      } else if (langs.length === 2 && selectedPair) {
        // Compare against selected pair
        const srcSel = normalizeCode(selectedPair.source);
        const tgtSel = normalizeCode(selectedPair.target);
        const tmxSet = new Set(langs);

        // Match if both selected codes are present in TMX
        const mismatch =
          !(tmxSet.has(srcSel) && tmxSet.has(tgtSel)) &&
          !(tmxSet.has(tgtSel) && tmxSet.has(srcSel)); // handle reversed

        if (mismatch) {
          const ok = window.confirm(
            "The languages in this TMX file do not match any of your language pairs. However, you can override the languages in the TMX file with your language pair. Do you want to continue?"
          );
          if (!ok) return;
          // record that the user accepted the override for this staged file
          setOverridePending(true);
        }
      }
    } catch (e) {
      // Non-fatal; let user proceed
      console.warn("TMX language detection failed:", e);
    }

    setDataUsage({ tmxFile: file, tmxFilename: name });

    // Upload to backend to obtain the S3 key for THIS file
    const emailForDraft = (data.lead?.email || "").trim();
    if (!emailForDraft) return;

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("email", emailForDraft);
      if (draftIdRef.current) {
        formData.append("draft_id", String(draftIdRef.current));
      }
      if (draftAccessTokenRef.current) {
        formData.append("draft_access_token", draftAccessTokenRef.current);
      }

      const resp = await fetch(`${BACKEND_URL}/workflow/tmx-file`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        console.error("Failed to upload TMX file", resp.status);
        setLastUploadedTmxKey("");
        return;
      }
      rememberDraftAccess(json);
      const key = (json && json.s3_key) || "";
      setLastUploadedTmxKey(key);
    } catch (err) {
      console.error("Error uploading TMX file", err);
      setLastUploadedTmxKey("");
    }
  };

  const addCurrentTmxToEntries = () => {
    if (!du.tmxFile || !du.tmxFilename || !selectedPair) {
      alert("Please choose a TMX file and select a language pair before committing.");
      return;
    }

    const nextEntries = [
      ...entries,
      {
        filename: du.tmxFilename,
        pairIndex: effectiveSelectedIndex,
        ...(lastUploadedTmxKey ? { s3_key: lastUploadedTmxKey } : {}),
		...(overridePending ? { override: true } : {}),
      },
    ];

    setDataUsage({
      entries: nextEntries,
      tmxFile: null,
      tmxFilename: "",
    });
    setLastUploadedTmxKey("");
	setOverridePending(false);
  };

  const removeEntry = (idxToRemove) => {
    const nextEntries = entries.filter((_, idx) => idx !== idxToRemove);
    setDataUsage({ entries: nextEntries });
  };

  return (
    <div>
      <h2 className="text-base font-semibold text-gray-900 mb-4">
        Data Usage / LLM Translation Training (via TMX)
      </h2>

      {/* Data Sharing */}
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Data Sharing</h3>
        <div className="space-y-2 text-sm text-gray-700">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="share_mode"
              checked={shareMode === "no_share"}
              onChange={() => setDataUsage({ shareMode: "no_share" })}
            />
            <span>
              Do not share my data or use it for training other models
            </span>
          </label>

          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="share_mode"
              checked={shareMode === "public_ok"}
              onChange={() => setDataUsage({ shareMode: "public_ok" })}
            />
            <span>My data can be shared publicly</span>
          </label>
        </div>

        <p className="mt-3 text-xs text-gray-600">
          Even with siloed data, we strongly encourage you to <b>not</b> share sensitive information.
          If you need a concrete siloed architecture, please contact us at{" "}
          <a href="mailto:support@zabtranslation.com" className="underline" style={{ color: "#00a7e1" }}>
            support@zabtranslation.com
          </a>
          .
        </p>
      </div>

      {/* Previous translations / TMX */}
      <div className="mt-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">
          Use My Previous Translations Corpus for LLM Translation Training (Optional)
        </h3>
        <p className="mt-3 text-xs text-gray-600">
          Only TMX files are accepted. You can add <b>one TMX file per language pair</b>.
        </p>

        {hasUnusedPairs && (
          <>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="mt-2 rounded-xl px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
              style={{ backgroundColor: "#00a7e1" }}
              disabled={!validPairs.length}
            >
              {entries.length > 0 ? "Add Additional TMX File" : "Choose TMX File"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".tmx"
              onChange={(e) => onTmxFile(e.target.files?.[0] || null)}
              className="hidden"
            />
          </>
        )}

        {du.tmxFilename && (
          <div className="mt-2 text-sm text-gray-700">
            <div>Selected (not yet added to the list):</div>
            <div className="flex items-center gap-3">
              <div className="font-semibold">
                {du.tmxFilename}
                {selectedPair && (
                  <>
                    {", "}
                    {selectedPair.source} {"\u2192"} {selectedPair.target}
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setDataUsage({ tmxFile: null, tmxFilename: "" });
                  setLastUploadedTmxKey("");
                }}
                className="text-sm text-red-600 underline"
              >
                Remove
              </button>
            </div>
          </div>
        )}

        {du.tmxFilename && validPairs.length > 1 && hasUnusedPairs && (
          <div className="mt-3 text-sm text-gray-700">
            <div className="mb-1 font-semibold">Which language pair does this TMX file apply to?</div>
            {validPairs.map((p, idx) => {
              if (usedPairIndices.has(idx)) return null;
              return (
                <label key={`${p.source}-${p.target}-${idx}`} className="flex items-center gap-2 mb-1">
                  <input
                    type="radio"
                    name="tmx_pair"
                    checked={effectiveSelectedIndex === idx}
                    onChange={() => setSelectedPairIndex(idx)}
                  />
                  {p.source} {"\u2192"} {p.target}
                </label>
              );
            })}
          </div>
        )}

        {du.tmxFilename && selectedPair && (
          <button
            type="button"
            onClick={addCurrentTmxToEntries}
            disabled={!du.tmxFilename}
            className={`mt-3 rounded-xl px-3 py-2 text-sm font-semibold text-white ${!du.tmxFilename ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"}`}
            style={{ backgroundColor: "#00a7e1" }}
          >
            Commit TMX for Language Pair
          </button>
        )}

        {entries.length > 0 && (
          <div className="mt-6 text-sm text-gray-700">
            <div className="font-semibold mb-1">TMX files added to this workflow:</div>
            <ul className="space-y-1">
              {entries.map((entry, idx) => {
                const pair =
                  typeof entry.pairIndex === "number" && validPairs[entry.pairIndex]
                    ? validPairs[entry.pairIndex]
                    : null;
                return (
                  <li key={`${entry.filename}-${idx}`} className="flex items-center gap-3">
                    <span>
                      {entry.filename}
                      {pair && (
                        <>
                          {", "}
                          {pair.source} {"\u2192"} {pair.target}
                        </>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeEntry(idx)}
                      className="text-xs text-red-600 underline"
                    >
                      Remove
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}


function PemtStep({ data, setData }) {
  return (
    <div>
      <h2 className="text-base font-semibold text-gray-900 mb-4">
        Post-AI Translation Review (PEMT)
      </h2>
      <div className="space-y-2">
        {PEMT_OPTIONS.map((opt) => (
          <label key={opt.key} className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="pemt"
              checked={data.pemt === opt.key}
              onChange={() => setData((d) => ({ ...d, pemt: opt.key }))}
            />
            {opt.label}
          </label>
        ))}
      </div>
    </div>
  );
}

function FormattingStep({ data, setData }) {
  return (
    <div>
      <h2 className="text-base font-semibold text-gray-900 mb-4">
        Post-Translation File Formatting
      </h2>
      <div className="space-y-2">
        {PT_FORMATTING.map((opt) => (
          <label key={opt.key} className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="formatting"
              checked={data.formatting === opt.key}
              onChange={() => setData((d) => ({ ...d, formatting: opt.key }))}
            />
            {opt.label}
          </label>
        ))}
      </div>
    </div>
  );
}


function SummaryStep({ data }) {
  const L = data.lead || {};
  const name =
    (L.firstName || L.lastName)
      ? [L.firstName, L.lastName].filter(Boolean).join(" ")
      : "Not provided";

  // Language pairs: only complete, valid pairs
  const validPairs = (data.pairs || []).filter(
    (p) => p.source && p.target && p.source !== p.target
  );

  // File types: show "Other (additional charges may apply)" instead of full free-text
  const fileTypes = data.fileTypes || [];
  const { otherSample } = data;
  const hasOther = fileTypes.includes(OTHER_LABEL);
  const fileTypesText = fileTypes
    .map((label) =>
      label === OTHER_LABEL
        ? "Other (additional charges may apply)"
        : label
    )
    .join(", ") || "None selected";

  const otherSampleLine = otherSample?.filename
    ? `Sample File for Other File Type: ${otherSample.filename}`
    : "";

  // Terminology
  const termEnabled = !!data.terminology?.enabled;
  const termEntries = Array.isArray(data.terminology?.entries)
    ? data.terminology.entries
    : [];
  const termStatus =
    termEnabled &&
    (termEntries.length > 0 || data.terminology?.filename)
      ? "Yes"
      : "No";

  const termFileLines = [];

  if (termEntries.length > 0) {
    termEntries.forEach((entry) => {
      const idx =
        typeof entry.pairIndex === "number" ? entry.pairIndex : null;
      const pair =
        idx !== null && validPairs[idx] ? validPairs[idx] : null;
      const pairText = pair
        ? `${pair.source} \u2192 ${pair.target}`
        : "Language pair not specified";

      termFileLines.push(
        `Terminology File: ${entry.filename} (${pairText})`
      );
    });
  } else if (data.terminology?.filename) {
    termFileLines.push(
      `Terminology File: ${data.terminology.filename}`
    );
  }

  // Data Usage / Translation Training
  const shareMode = data.dataUsage?.shareMode || "no_share";
  const dataUsageText =
    shareMode === "public_ok"
      ? "Share data publicly"
      : "Do not share my data";

  const tmxFilename = data.dataUsage?.tmxFilename || "";
  const tmxLine = tmxFilename
    ? `Data Training File: ${tmxFilename}`
    : "";

  // PEMT label
  const pemtOption = PEMT_OPTIONS.find((opt) => opt.key === data.pemt);
  const pemtText = pemtOption ? pemtOption.label : "Not specified";

  // Formatting label
  const fmtOption = PT_FORMATTING.find((opt) => opt.key === data.formatting);
  const fmtText = fmtOption ? fmtOption.label : "Not specified";

  return (
    <div>
      <h2 className="text-base font-semibold text-gray-900 mb-4">
        Custom Workflow Summary
      </h2>

      <div className="space-y-2 text-sm text-gray-800">
        <p>
          <span className="font-semibold">Name:</span>{" "}
          {name}
        </p>
        <p>
          <span className="font-semibold">E-mail Address:</span>{" "}
          {L.email || "Not provided"}
        </p>
        <p>
          <span className="font-semibold">Language Pair(s):</span>{" "}
          {validPairs.length === 0 ? "None selected" : ""}
        </p>
        {validPairs.length > 0 && (
          <ul className="ml-4 mt-1 list-disc text-sm text-gray-800">
            {validPairs.map((p, idx) => (
              <li key={`${p.source}-${p.target}-${idx}`}>
                {p.source} {"\u2192"} {p.target}
              </li>
            ))}
          </ul>
        )}
        <p>
          <span className="font-semibold">File Type(s):</span>{" "}
          {fileTypesText}
        </p>
        {otherSampleLine && (
          <p className="ml-4">{otherSampleLine}</p>
        )}

        <p className="mt-3">
          <span className="font-semibold">Terminology Enforcement:</span>{" "}
          {termStatus}
        </p>
        {termFileLines.map((line, idx) => (
          <p key={idx} className="ml-4">
            {line}
          </p>
        ))}

        <p className="mt-3">
          <span className="font-semibold">Data Usage / LLM Translation Training:</span>{" "}
          {dataUsageText}
        </p>
        {tmxLine && (
          <p className="ml-4">{tmxLine}</p>
        )}

        <p className="mt-3">
          <span className="font-semibold">Post-AI Translation Review (PEMT):</span>{" "}
          {pemtText}
        </p>

        <p className="mt-3">
          <span className="font-semibold">Post-Translation File Formatting:</span>{" "}
          {fmtText}
        </p>
      </div>

      <p className="mt-6 text-xs text-gray-500">
        Use the <b>Back</b> button below if you need to adjust any step before completing
        your custom workflow.
      </p>
    </div>
  );
}


/* ===== Lead Capture / Save Progress Modal ===== */
function LeadCaptureModal({
  data,
  setData,
  captchaToken,
  onCaptchaChange,
  error,
  saving,
  onClose,
  onSave,
}) {
  const L = data.lead || {};
  const setLead = (patch) => setData((d) => ({ ...d, lead: { ...d.lead, ...patch } }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-lg">
        <h3 className="text-base font-semibold text-gray-900">Save Your Progress?</h3>
        <p className="text-sm text-gray-600 mt-1">
          We can email you a link to finish setup later.
        </p>
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-700 mb-1">First Name</label>
              <input
                type="text"
                value={L.firstName}
                onChange={(e) => setLead({ firstName: e.target.value })}
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                disabled={saving}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Last Name</label>
              <input
                type="text"
                value={L.lastName}
                onChange={(e) => setLead({ lastName: e.target.value })}
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                disabled={saving}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={L.email}
              onChange={(e) => setLead({ email: e.target.value })}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
              disabled={saving}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Company (optional)</label>
            <input
              type="text"
              value={L.company}
              onChange={(e) => setLead({ company: e.target.value })}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
              disabled={saving}
            />
          </div>

          <div>
            <ReCAPTCHA
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={(token) => onCaptchaChange(token || "")}
              onExpired={() => onCaptchaChange("")}
              onErrored={() => onCaptchaChange("")}
            />
          </div>

          {error && (
            <div className="text-sm text-red-700" role="alert">
              {error}
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="rounded-xl border border-gray-300 px-4 py-2 text-sm disabled:opacity-50"
          >
            No Thanks
          </button>
          <button
            onClick={onSave}
            disabled={saving || !captchaToken}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: "#00a7e1" }}
          >
            {saving ? "Saving..." : "Save & Exit"}
          </button>
        </div>
      </div>
    </div>
  );
}
